import React, { useEffect, useRef, useState } from 'react';
import * as bootstrap from 'bootstrap';
import API from '../../config/config';

export default function ViewPhotosModal({ show, eventPhotos = [], handleClose }) {
  const modalRef = useRef(null);
  const modalInstance = useRef(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isSlideshow, setIsSlideshow] = useState(false);
  const [slideshowInterval, setSlideshowInterval] = useState(null);
  const [imageLoading, setImageLoading] = useState(true);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Initialize the modal once
  useEffect(() => {
    const modalNode = modalRef.current;
    if (modalNode) {
      modalInstance.current = new bootstrap.Modal(modalNode, {
        backdrop: 'static',
        keyboard: false,
      });

      const handleHidden = () => {
        handleClose();
        stopSlideshow();
        setCurrentImageIndex(0);
        setZoomLevel(1);
      };
      modalNode.addEventListener('hidden.bs.modal', handleHidden);

      return () => {
        if (modalInstance.current) {
          modalInstance.current.dispose();
        }
        modalNode.removeEventListener('hidden.bs.modal', handleHidden);
        stopSlideshow();
      };
    }
  }, [handleClose]);

  // Show or hide the modal
  useEffect(() => {
    if (!modalInstance.current) return;
    
    if (show) {
      modalInstance.current.show();
      setCurrentImageIndex(0);
    } else {
      modalInstance.current.hide();
    }
  }, [show]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!show) return;
      
      switch(e.key) {
        case 'ArrowLeft':
          navigateImage('prev');
          break;
        case 'ArrowRight':
          navigateImage('next');
          break;
        case 'Escape':
          if (isFullscreen) exitFullscreen();
          break;
        case ' ':
          e.preventDefault();
          toggleSlideshow();
          break;
        case '+':
        case '=':
          zoomIn();
          break;
        case '-':
          zoomOut();
          break;
        case '0':
          resetZoom();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [show, currentImageIndex, isSlideshow, isFullscreen, zoomLevel]);

  // Slideshow functionality
  const startSlideshow = () => {
    setIsSlideshow(true);
    const interval = setInterval(() => {
      setCurrentImageIndex(prev => 
        prev === eventPhotos.length - 1 ? 0 : prev + 1
      );
    }, 3000);
    setSlideshowInterval(interval);
  };

  const stopSlideshow = () => {
    setIsSlideshow(false);
    if (slideshowInterval) {
      clearInterval(slideshowInterval);
      setSlideshowInterval(null);
    }
  };

  const toggleSlideshow = () => {
    if (isSlideshow) {
      stopSlideshow();
    } else {
      startSlideshow();
    }
  };

  // Navigation functions
  const navigateImage = (direction) => {
    setImageLoading(true);
    if (direction === 'next') {
      setCurrentImageIndex(prev => 
        prev === eventPhotos.length - 1 ? 0 : prev + 1
      );
    } else {
      setCurrentImageIndex(prev => 
        prev === 0 ? eventPhotos.length - 1 : prev - 1
      );
    }
    resetZoom();
  };

  // Zoom functions
  const zoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.25, 3));
  };

  const zoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
  };

  const resetZoom = () => {
    setZoomLevel(1);
  };

  // Fullscreen functions
  const enterFullscreen = () => {
    const modal = modalRef.current;
    if (modal.requestFullscreen) {
      modal.requestFullscreen();
      setIsFullscreen(true);
    }
  };

  const exitFullscreen = () => {
    if (document.exitFullscreen) {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Touch handlers for mobile swipe
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      navigateImage('next');
    }
    if (isRightSwipe) {
      navigateImage('prev');
    }
  };

  // Download image function
  const downloadImage = (photo) => {
    const link = document.createElement('a');
    link.href = photo.url;
    link.download = `event-photo-${currentImageIndex + 1}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const currentPhoto = eventPhotos[currentImageIndex];

  return (
    <div
      className="modal fade"
      tabIndex="-1"
      aria-hidden="true"
      ref={modalRef}
    >
      <div className={`modal-dialog ${isFullscreen ? 'modal-fullscreen' : 'modal-xl'} modal-dialog-centered`}>
        <div className="modal-content bg-dark text-white" style={{ minHeight: '80vh' }}>
          {/* Enhanced Header */}
          <div className="modal-header border-secondary bg-dark">
            <div className="d-flex align-items-center">
              <h5 className="modal-title me-3">
                <i className="fa fa-camera me-2"></i>
                Event Gallery
              </h5>
              <span className="badge bg-primary">
                {currentImageIndex + 1} of {eventPhotos.length}
              </span>
            </div>
            
            {/* Control Buttons */}
            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="btn btn-outline-info btn-sm"
                onClick={toggleSlideshow}
                title={isSlideshow ? 'Stop Slideshow' : 'Start Slideshow'}
              >
                <i className={`fa fa-${isSlideshow ? 'pause' : 'play'}`}></i>
              </button>
              
              <button
                type="button"
                className="btn btn-outline-warning btn-sm"
                onClick={isFullscreen ? exitFullscreen : enterFullscreen}
                title="Toggle Fullscreen"
              >
                <i className={`fa fa-${isFullscreen ? 'compress' : 'expand'}`}></i>
              </button>
              
              {currentPhoto && (
                <button
                  type="button"
                  className="btn btn-outline-success btn-sm"
                  onClick={() => downloadImage(currentPhoto)}
                  title="Download Image"
                >
                  <i className="fa fa-download"></i>
                </button>
              )}
              
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={() => {
                  if (modalInstance.current) {
                    modalInstance.current.hide();
                  }
                }}
                aria-label="Close"
              ></button>
            </div>
          </div>

          <div className="modal-body p-0 position-relative">
            {eventPhotos.length > 0 ? (
              <div className="photo-gallery-container">
                {/* Main Image Display */}
                <div 
                  className="main-image-container position-relative"
                  style={{ 
                    height: isFullscreen ? '90vh' : '70vh',
                    background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  {/* Navigation Arrows */}
                  <button
                    className="btn btn-dark btn-lg position-absolute start-0 top-50 translate-middle-y ms-3"
                    style={{ zIndex: 10, opacity: 0.8 }}
                    onClick={() => navigateImage('prev')}
                    disabled={eventPhotos.length <= 1}
                  >
                    <i className="fa fa-chevron-left"></i>
                  </button>
                  
                  <button
                    className="btn btn-dark btn-lg position-absolute end-0 top-50 translate-middle-y me-3"
                    style={{ zIndex: 10, opacity: 0.8 }}
                    onClick={() => navigateImage('next')}
                    disabled={eventPhotos.length <= 1}
                  >
                    <i className="fa fa-chevron-right"></i>
                  </button>

                  {/* Loading Spinner */}
                  {imageLoading && (
                    <div className="position-absolute top-50 start-50 translate-middle">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  )}

                  {/* Main Image */}
                  {currentPhoto && (
                    <img
                      src={currentPhoto.url}
                      alt={`Event photo ${currentImageIndex + 1}`}
                      className="img-fluid"
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '100%', 
                        objectFit: 'contain',
                        transform: `scale(${zoomLevel})`,
                        transition: 'transform 0.3s ease',
                        cursor: zoomLevel > 1 ? 'grab' : 'pointer'
                      }}
                      onLoad={() => setImageLoading(false)}
                      onError={() => setImageLoading(false)}
                      onClick={() => zoomLevel === 1 ? zoomIn() : resetZoom()}
                    />
                  )}

                  {/* Image Info Overlay */}
                  <div 
                    className="position-absolute bottom-0 start-0 end-0 p-3"
                    style={{ 
                      background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                      color: 'white'
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="mb-1">Photo {currentImageIndex + 1}</h6>
                        {currentPhoto?.timestamp && (
                          <small className="text-light">
                            <i className="fa fa-clock me-1"></i>
                            {new Date(currentPhoto.timestamp).toLocaleString()}
                          </small>
                        )}
                      </div>
                      
                      {/* Zoom Controls */}
                      <div className="btn-group btn-group-sm">
                        <button 
                          className="btn btn-outline-light"
                          onClick={zoomOut}
                          disabled={zoomLevel <= 0.5}
                        >
                          <i className="fa fa-minus"></i>
                        </button>
                        <button 
                          className="btn btn-outline-light"
                          onClick={resetZoom}
                        >
                          {Math.round(zoomLevel * 100)}%
                        </button>
                        <button 
                          className="btn btn-outline-light"
                          onClick={zoomIn}
                          disabled={zoomLevel >= 3}
                        >
                          <i className="fa fa-plus"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Enhanced Thumbnail Strip */}
                <div 
                  className="thumbnail-strip p-3"
                  style={{ 
                    background: '#1a1a1a',
                    borderTop: '1px solid #333'
                  }}
                >
                  <div 
                    className="d-flex gap-2"
                    style={{ 
                      overflowX: 'auto',
                      scrollBehavior: 'smooth',
                      paddingBottom: '10px'
                    }}
                  >
                    {eventPhotos.map((photo, index) => (
                      <div
                        key={index}
                        className={`thumbnail-item ${index === currentImageIndex ? 'active' : ''}`}
                        style={{
                          minWidth: '80px',
                          height: '80px',
                          cursor: 'pointer',
                          border: index === currentImageIndex ? '3px solid #0d6efd' : '2px solid transparent',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          transition: 'all 0.3s ease',
                          transform: index === currentImageIndex ? 'scale(1.1)' : 'scale(1)',
                          opacity: index === currentImageIndex ? 1 : 0.7
                        }}
                        onClick={() => {
                          setCurrentImageIndex(index);
                          setImageLoading(true);
                          resetZoom();
                        }}
                      >
                        <img
                          src={photo.url}
                          alt={`Thumbnail ${index + 1}`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Progress Bar for Slideshow */}
                {isSlideshow && (
                  <div 
                    className="position-absolute top-0 start-0 end-0"
                    style={{ height: '4px', background: 'rgba(0,0,0,0.3)' }}
                  >
                    <div 
                      className="bg-primary h-100"
                      style={{
                        width: '100%',
                        animation: 'slideProgress 3s linear infinite'
                      }}
                    ></div>
                  </div>
                )}
              </div>
            ) : (
              <div className="d-flex flex-column align-items-center justify-content-center" style={{ height: '400px' }}>
                <i className="fa fa-image fa-4x text-muted mb-3"></i>
                <h5 className="text-muted">No photos available</h5>
                <p className="text-muted">Photos will appear here once uploaded</p>
              </div>
            )}
          </div>

          {/* Keyboard Shortcuts Help */}
          <div className="modal-footer border-secondary bg-dark">
            <small className="text-muted">
              <strong>Shortcuts:</strong> ← → Navigate | Space Slideshow | + - Zoom | Esc Exit Fullscreen
            </small>
          </div>
        </div>
      </div>

      {/* Custom Styles */}
      <style jsx="true">{`
        @keyframes slideProgress {
          from { transform: translateX(-100%); }
          to { transform: translateX(0%); }
        }
        
        .thumbnail-strip::-webkit-scrollbar {
          height: 6px;
        }
        
        .thumbnail-strip::-webkit-scrollbar-track {
          background: #2d2d2d;
          border-radius: 3px;
        }
        
        .thumbnail-strip::-webkit-scrollbar-thumb {
          background: #555;
          border-radius: 3px;
        }
        
        .thumbnail-strip::-webkit-scrollbar-thumb:hover {
          background: #777;
        }
        
        .main-image-container img {
          transition: transform 0.3s ease;
        }
        
        .btn:focus {
          box-shadow: none;
        }
        
        .modal-fullscreen .modal-content {
          height: 100vh;
        }
      `}</style>
    </div>
  );
}